/**
 * PropertySelector Component (Story 4.6, Task 4; Story 4.7, Task 4.1)
 *
 * Modal dialog for selecting a property/site after Google OAuth completes.
 * Platform-generic: supports GA4, GSC, and future integrations via props.
 * Uses Radix Dialog (via @provenleap/ui) matching IntegrationPickerModal visual style.
 *
 * ADR-4.6-C: Property Selector as Modal (Not Inline or Drawer)
 *
 * Visual states:
 * 1. Loading skeleton while fetching properties
 * 2. Property list grouped by account name with radio selection
 * 3. Error state with descriptive message and retry button
 * 4. Empty state if no properties found
 */

import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@provenleap/ui";
import { Button } from "@provenleap/ui";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { apiGet, apiPut } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types
// ============================================================================

interface AccountInfo {
  id: string;
  name: string;
  platform: string;
  metadata?: {
    accountName?: string;
    parentAccountId?: string;
    propertyResource?: string;
    propertyType?: string;
    propertySubtitle?: string;
    isManager?: boolean;
  };
}

interface PropertySelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integrationId: string;
  showToast: (type: ToastType, message: string) => void;
  onPropertySelected?: () => void;
  /** Dialog title. Default: "Select Property" */
  title?: string;
  /** Dialog description. Default: "Choose the property you want to connect to this client." */
  description?: string;
  /** Empty state message. Default: "No properties found." */
  emptyMessage?: string;
  /** HTML id/name prefix for radio inputs. Default: "property" */
  idPrefix?: string;
}

// ============================================================================
// Component
// ============================================================================

export function PropertySelector({
  open,
  onOpenChange,
  integrationId,
  showToast,
  onPropertySelected,
  title = "Select Property",
  description = "Choose the property you want to connect to this client.",
  emptyMessage = "No properties found.",
  idPrefix = "property",
}: PropertySelectorProps) {
  const [properties, setProperties] = useState<AccountInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<AccountInfo | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [filterText, setFilterText] = useState("");

  // Fetch properties when modal opens
  const fetchProperties = useCallback(async () => {
    if (!integrationId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiGet(
        `/api/integrations/${integrationId}/accounts`
      );

      if (!response.ok) {
        const status = response.status;
        if (status === 401) {
          setError(
            "Your Google connection has expired. Please reconnect."
          );
        } else if (status === 403) {
          setError(
            "You don't have permission to access this property."
          );
        } else if (status === 404) {
          setError(
            "Integration connection not found. Please reconnect."
          );
        } else if (status === 429) {
          setError(
            "Rate limit reached. Please try again in a few minutes."
          );
        } else if (status === 502) {
          setError(
            "Google is temporarily unavailable. Please try again later."
          );
        } else {
          setError("Failed to load properties. Please try again.");
        }
        return;
      }

      const result = (await response.json()) as { data: AccountInfo[] };
      setProperties(result.data);
    } catch {
      setError(
        "Unable to reach the server. Check your internet connection."
      );
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (open) {
      fetchProperties();
      setSelectedProperty(null);
      setFilterText("");
    }
  }, [open, fetchProperties]);

  // Handle property selection confirmation
  const handleConfirm = useCallback(async () => {
    if (!selectedProperty) return;

    setSaving(true);

    try {
      const response = await apiPut(
        `/api/integrations/${integrationId}/account`,
        {
          accountId: selectedProperty.id,
          accountName: selectedProperty.name,
          ...(selectedProperty.metadata?.parentAccountId
            ? { parentAccountId: selectedProperty.metadata.parentAccountId as string }
            : {}),
        }
      );

      if (!response.ok) {
        const status = response.status;
        if (status === 401) {
          showToast(
            "error",
            "Your Google connection has expired. Please reconnect."
          );
        } else if (status === 403) {
          showToast(
            "error",
            "You don't have permission to configure this integration."
          );
        } else {
          showToast("error", "Failed to save property selection. Please try again.");
        }
        return;
      }

      showToast("success", `Connected to ${selectedProperty.name}`);
      onOpenChange(false);
      onPropertySelected?.();
    } catch {
      showToast(
        "error",
        "Unable to reach the server. Check your internet connection."
      );
    } finally {
      setSaving(false);
    }
  }, [selectedProperty, integrationId, showToast, onOpenChange, onPropertySelected]);

  // Filter properties by search text (matches name and propertySubtitle)
  const filteredProperties = filterText
    ? properties.filter((prop) => {
        const lower = filterText.toLowerCase();
        return (
          prop.name.toLowerCase().includes(lower) ||
          (prop.metadata?.propertySubtitle ?? "").toLowerCase().includes(lower)
        );
      })
    : properties;

  // Check if all items lack accountName — render flat list if so (AC #16)
  const allMissingAccountName = filteredProperties.every(
    (prop) => !prop.metadata?.accountName
  );

  // Group properties by account name (skip grouping for flat list)
  const groupedProperties = allMissingAccountName
    ? { "": filteredProperties }
    : filteredProperties.reduce<Record<string, AccountInfo[]>>(
        (groups, prop) => {
          const accountName =
            prop.metadata?.accountName ?? "Unknown Account";
          if (!groups[accountName]) {
            groups[accountName] = [];
          }
          groups[accountName].push(prop);
          return groups;
        },
        {}
      );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 max-h-80 overflow-y-auto">
          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="mt-2 text-sm">Loading properties...</p>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="mt-2 text-sm text-destructive">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={fetchProperties}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && properties.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <p className="text-sm">{emptyMessage}</p>
            </div>
          )}

          {/* Filter input */}
          {!loading && !error && properties.length > 3 && (
            <div className="mb-3">
              <input
                type="text"
                placeholder="Filter by name or address..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          )}

          {/* Property list grouped by account */}
          {!loading &&
            !error &&
            filteredProperties.length > 0 &&
            Object.entries(groupedProperties).map(
              ([accountName, accountProperties]) => (
                <div key={accountName || "flat"} className="mb-4">
                  {accountName && (
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {accountName}
                    </h4>
                  )}
                  <div className="space-y-1">
                    {accountProperties.map((prop) => (
                      <label
                        key={prop.id}
                        htmlFor={`${idPrefix}-${prop.id}`}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors hover:bg-accent ${
                          selectedProperty?.id === prop.id
                            ? "border-primary bg-accent"
                            : "border-transparent"
                        }`}
                      >
                        <input
                          id={`${idPrefix}-${prop.id}`}
                          type="radio"
                          name={idPrefix}
                          value={prop.id}
                          checked={selectedProperty?.id === prop.id}
                          onChange={() => setSelectedProperty(prop)}
                          className="h-4 w-4 accent-primary"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">
                              {prop.name}
                            </p>
                            {prop.metadata?.propertyType && (
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                {prop.metadata.propertyType === "domain"
                                  ? "Domain"
                                  : "URL prefix"}
                              </span>
                            )}
                            {prop.metadata?.isManager && (
                              <span className="shrink-0 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-200">
                                Manager
                              </span>
                            )}
                          </div>
                          {prop.metadata?.propertySubtitle ? (
                            <p className="text-xs text-muted-foreground">
                              {prop.metadata.propertySubtitle}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              ID: {/^\d{7,10}$/.test(prop.id) ? prop.id.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3") : prop.id}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )
            )}
        </div>

          {/* No results after filtering */}
          {!loading && !error && properties.length > 0 && filteredProperties.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <p className="text-sm">No matching locations found.</p>
            </div>
          )}

        {/* Footer with confirm button */}
        {!loading && !error && filteredProperties.length > 0 && (
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedProperty || saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect Property"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
